import React, {useEffect} from "react";
import {useFormContext} from "react-hook-form";
import {
    AuthorizationInfo,
    isAssessmentAuth,
    YesNoOption,
    YesNoOptionMap,
} from "../../domain";
import {TreatmentHoldInput} from "./TreatmentHoldFormModal";
import {ModalDatePicker} from "../../form-components/ModalDatePicker";
import {ModalSelectionInput} from "../../form-components/ModalSelectionInput";
import {TreatmentHoldOfferHoursInput} from "./TreatmentHoldOfferHoursInput";

type Props = {
    sending: boolean;
    authorization?: AuthorizationInfo;
};

export function TreatmentHoldWorkflowFields({sending, authorization}: Props) {
    const {watch, setValue} = useFormContext<TreatmentHoldInput>();
    const canResume = watch("canProviderResumeServices");
    const canOfferFso = watch("canProviderOfferFso");
    const resumeYes = canResume === YesNoOption.YES;
    const resumeNo = canResume === YesNoOption.NO;
    const offerFsoYes = canOfferFso === YesNoOption.YES;
    const offerFsoNo = canOfferFso === YesNoOption.NO;
    const assessmentAuth = isAssessmentAuth(authorization);

    useEffect(() => {
        if (!resumeYes) {
            setValue("canProviderOfferFso", "");
            setValue("offeredParaprofessionalHours", null);
            setValue("offeredMidLevelHours", null);
            setValue("offeredHighLevelHours", null);
            setValue("offeredGroupCareHours", null);
        }
    }, [resumeYes, setValue]);

    useEffect(() => {
        if (!resumeNo) {
            setValue("familyNotifiedUnableToResume", null);
            setValue("dateOfLastTreatment", null);
            setValue("dateOfLastFullScheduleOffer", null);
        }
    }, [resumeNo, setValue]);

    useEffect(() => {
        if (!offerFsoYes) {
            setValue("offeredParaprofessionalHours", null);
            setValue("offeredMidLevelHours", null);
            setValue("offeredHighLevelHours", null);
            setValue("offeredGroupCareHours", null);
        }
    }, [offerFsoYes, setValue]);

    return (
        <div className="border-t border-gray-300 mt-4 pt-4">
            <ModalSelectionInput<TreatmentHoldInput>
                options={YesNoOptionMap}
                label="Can provider resume services after Treatment Hold?"
                labelClassName="font-bold"
                disabled={sending}
                field="canProviderResumeServices"
                optionalRules={{required: "Please select 'Resume services after Treatment Hold?' before submitting"}}
            />

            {resumeYes && (
                <ModalSelectionInput<TreatmentHoldInput>
                    options={YesNoOptionMap}
                    label="Can provider offer FSO after Treatment Hold end date?"
                    labelClassName="font-bold"
                    disabled={sending}
                    field="canProviderOfferFso"
                    optionalRules={{required: "Please select 'Provider offer FSO after Treatment Hold end date?' before submitting"}}
                />
            )}

            {resumeNo && (
                <>
                    <ModalSelectionInput<TreatmentHoldInput>
                        options={YesNoOptionMap}
                        label="Has family been notified about inability to resume and need to transfer been discussed?"
                        disabled={sending}
                        field="familyNotifiedUnableToResume"
                        optionalRules={{required: "Please select 'Has family been notified about inability to resume?' before submitting"}}
                    />
                    <ModalDatePicker<TreatmentHoldInput>
                        disabled={sending}
                        label="Date of Last Treatment"
                        field="dateOfLastTreatment"
                        optionalRules={{required: "This field is required."}}
                    />
                    <ModalDatePicker<TreatmentHoldInput>
                        disabled={sending}
                        label={'Date of Last Full Schedule Offer'}
                        field={'dateOfLastFullScheduleOffer'}
                        optionalRules={{required: 'This field is required.'}}
                    />
                </>
            )}

            {resumeYes && offerFsoYes && !assessmentAuth && authorization && (
                <TreatmentHoldOfferHoursInput disabled={sending} authorization={authorization}/>
            )}

            {resumeYes && offerFsoNo && !assessmentAuth && (
                <div className="flex w-full py-3">
                    <div className="w-2/6"/>
                    <div className="w-4/6 ml-4 text-red-700">
                        Staffing Plan Required if FSO cannot be offered, you will be redirected upon submission of this form
                    </div>
                </div>
            )}
        </div>
    );
}
